import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react'
import { allWindows, addAsyncComponent } from 'data/systemWindows'
import { useSelector, useDispatch } from 'react-redux'
import {
  maxZ,
  modalZ,
  coveredModalZ
} from '../../../reducers/interactableReducer.js'
import { setInteractableContainer, updateInteractableContainer, closeWindow } from 'actions/interactableActions'
import { setWindow } from '../../../actions/interactableActions.js'
import { useContext } from 'hooks/useURL'

const WindowContainer = ({ inWorkflow, extraWindowProps = {}, children, verticalOffset = '0', horizontalOffset = '0' }) => {
  const dispatch = useDispatch()
  const dragContainer = useRef(0)
  const updateContainerRef = useRef()
  const [renderWindows, setRenderWindows] = useState([])

  const windowMessaging = useSelector(state => state.windowMessaging)
  const windows = useSelector(state => state.interactableState.windows) || {}

  const zIndexHandler = useCallback(refName => {
    if (windows[refName]) {
      dispatch(setWindow(windows[refName]))
    }
  }, [windows])

  const updateContainer = useCallback(() => {
    if (dragContainer.current != null) {
      const { width, height } = window.getComputedStyle(dragContainer.current)
      dispatch(
        updateInteractableContainer(
          width,
          height,
          verticalOffset,
          horizontalOffset
        )
      )
    }
  }, [verticalOffset, horizontalOffset])

  useEffect(() => {
    // going in and out of a workflow would change the size of the drag container
    updateContainer()
  }, [inWorkflow])

  useEffect(() => {
    updateContainer()
  }, [verticalOffset, horizontalOffset])

  // update container will be called from an event listener but because its context will change over time
  // (aka offset may adjust) its being stored in a ref that is kept up to date, then the listener executes
  // whatever function ref refers to.
  useEffect(() => {
    updateContainerRef.current = updateContainer
  }, [updateContainer])

  useEffect(() => {
    // componentDidMount
    updateContainer()
    // add a listener to update the window size as necessary
    const handleResize = () => updateContainerRef.current()
    window.addEventListener('resize', handleResize)
    return () => {
      // componentWillUnmount
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const context = useContext()
  useEffect(() => {
    // all windows that should be rendered
    const requestedWindows = allWindows
      .filter(window => window.contexts.find(config => config.context === context) != null)
      .filter(componentConfig => Object.keys(windows).find(window => window === componentConfig.id) != null)
    // subset of displayed windows that need to be rendered but were not in the last render.
    const newWindows = requestedWindows.filter(componentConfig => renderWindows.find(window => window.id === componentConfig.id) == null)
      .map(config => addAsyncComponent(config, context))
    // subset of previously rendered windows that should not be included in the next render (they were closed)
    const alreadyOpenWindows = renderWindows.filter(componentConfig => requestedWindows.find(window => window.id === componentConfig.id) != null)
    const nextDisplayedWindows = alreadyOpenWindows.concat(newWindows)
      .map(componentConfig => {
        const windowConfig = windows[componentConfig.id]
        // copy the context based config (privileges, etc) from the component config to the
        // window config so its available for future references.
        const contextConfig = componentConfig.contexts?.find(possibleConfig => possibleConfig.context === context) || {}
        const { windowConfig: contextWindowConfig = {}, ...rest } = contextConfig

        componentConfig.windowConfig = { ...componentConfig.windowConfig, ...contextWindowConfig, ...windowConfig, context: rest }

        return componentConfig
      })

    setRenderWindows(nextDisplayedWindows)
  }, [windows])

  return (
    <div
        className="draggable-container"
        ref={dragContainer}
        data-position="auto"
      >
      {children}
      {
        // this is a special case indicating to not render any windows for this cycle while the parent tries to
        // take measurements of some UI element to calculate the available space for windows. By skipping a
        // render while this happens, we avoid a weird initial load, and then shift, of all windows.
        verticalOffset === 'loading' || horizontalOffset === 'loading'
          ? null
          : renderWindows.map((componentConfig, idx) => {
            const Component = componentConfig.component
            const extraProps = componentConfig.extraProps || {}
            return (
              <ContainerChild
                key={componentConfig.windowConfig.ref}
                child={
                  <Component
                    helpId = {`${componentConfig.id.toUpperCase()}_WIDGET`}
                    windowName = {componentConfig.id}
                    className = {`${componentConfig.id}-window`}
                    {...extraProps}
                    {...extraWindowProps}
                  />
                }
                windowConfig={componentConfig.windowConfig}
                windowMessages={windowMessaging[componentConfig.id]}
                zIndexHandler={zIndexHandler}
              />
            )
          })
      }
    </div>
  )
}

export default WindowContainer

const ContainerChild = ({
  child,
  windowConfig,
  windowMessages,
  zIndexHandler: propsZIndexHandler
}) => {
  const container = useSelector(state => state.interactableState.container)
  const windowName = child.props.windowName
  const windowRef = useRef(0)
  const dispatch = useDispatch()

  const zIndexHandler = useCallback(() => {
    propsZIndexHandler(windowName)
  }, [windowName])

  const closeHandler = useCallback(() => {
    dispatch(closeWindow(windowName))
  }, [windowName])

  const coverOpacity = useMemo(() => {
    if (!windowConfig.modalConfig) {
      // this isn't a modal configured window so the opacity is irrelevant.
      return 0
    }
    return windowConfig.modalConfig.cover === true
      ? 0.5
      : windowConfig.modalConfig.cover
  }, [windowConfig.modalConfig])

  useEffect(() => {
    // Keep window locations in sync with state
    if (!windowConfig || !windowRef.current || !windowRef.current.style) {
      // not an open window so do nothing
      return
    }
 
    let zIndex = windowConfig.zIndex
    if (
      zIndex == null &&
      windowConfig.asModal &&
      windowConfig.modalConfig &&
      windowConfig.modalConfig.cover != null
    ) {
      zIndex = coveredModalZ
    } else if (zIndex == null && windowConfig.asModal) {
      zIndex = modalZ
    } else if (zIndex == null) {
      zIndex = maxZ
    }
    // loading means we are in the process of measuring something like a header
    const loading = container.verticalOffset === 'loading' || container.horizontalOffset === 'loading'
    // adjust the windows location
    windowRef.current.style.visibility = loading ? 'hidden' : 'visible' // only visible after loading complete
    windowRef.current.style.width = windowConfig.width + 'px'
    windowRef.current.style.height = windowConfig.height + 'px'
    windowRef.current.style.transform = `translate(${windowConfig.x}px,${windowConfig.y}px)`
    windowRef.current.style.zIndex = zIndex
  }, [
    windowConfig.zIndex,
    windowConfig.asModal,
    windowConfig.modalConfig?.cover,
    windowConfig.width,
    windowConfig.height,
    windowConfig.x,
    windowConfig.y,
    windowRef.current,
    container.verticalOffset,
    container.horizontalOffset
  ])

  // For each child of the windowContainer, add the zIndexHandler and close handler
  if (
    windowConfig.asModal &&
    windowConfig.modalConfig &&
    windowConfig.modalConfig.cover
  ) {
    // this is a modal window so we need the element and the screen cover.
    return (
      <div
        className="modal-window-screen-cover"
        style={{
          zIndex: 100000,
          backgroundColor: `rgba(0,0,0,${coverOpacity})`
        }}
      >
        {React.cloneElement(child, {
          windowRef, // this is a bit hacky but i'm having trouble passing ref through all of the
          // required levels to get it to asWindow.
          windowMessages,
          windowConfig,
          zIndexHandler,
          closeHandler
        })}
      </div>
    )
  } else {
    // its in the interactableState so show it
    return React.cloneElement(child, {
      windowRef, // this is a bit hacky but i'm having trouble passing ref through all of the
      // required levels to get it to asWindow.
      windowMessages,
      windowConfig,
      zIndexHandler,
      closeHandler
    })
  }
}